import { useEffect, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Undo2, ArrowLeft } from 'lucide-react';
import { useLanguage } from '../i18n/LanguageContext';

import EnterInfoStep from './EnterInfoStep.tsx';
import ChooseDoctorOrTime from './ChooseDoctorOrTime';
import { scrollToTop } from '../utils/scrollToTop';
import { useIsMobile } from '../hooks/use-mobile.tsx';
import StepProgress from '../components/booking/StepProgress.js';
import { ChooseMethod } from './ChooseMethod';

const BookingFlow = () => {
  const [isMobile, resetIsMobile] = useIsMobile();
  const { t } = useLanguage();

  const [searchParams, setSearchParams] = useSearchParams();
  const type = searchParams.get('type');
  const flowType = searchParams.get('flow');
  const stepKey = searchParams.get('step');
  const selectedSpecialty = searchParams.get('specialty');
  const navigate = useNavigate();

  const steps = [
    {
      key: null, label: t('chonphuongthuckham'),
      element: <ChooseMethod
        flowType={flowType}
        stepKey={stepKey}
      />
    },
    {
      key: 'chooseTime', label: t('chonthoigiankham'),
      element: <ChooseDoctorOrTime
        flowType={flowType}
        stepKey={stepKey}
      />
    },
    {
      key: 'enterInfo', label: t('nhaphosoKham'),
      element: <EnterInfoStep isMobile={isMobile} />
    },
    {
      key: 'chooseDoctor', label: t('chonbacsi'),
      element: <ChooseDoctorOrTime
        flowType={flowType}
        stepKey={stepKey}
      />
    }
  ];

  const onBack = (prevStepKey) => {
    navigate(-1);
    if (prevStepKey === 'home') {
      return;
    }
    
    updateStep(prevStepKey);

    scrollToTop();
    resetIsMobile();
  };

  const updateStep = (nextStepKey) => {
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      newParams.set('step', nextStepKey);
      return newParams;
    });
  };

  const currentStepIndex = useMemo(() => {
    let index = steps.findIndex(step => step.key === stepKey);

    return index;
  }, [isMobile, stepKey, steps]);

  const currentStepComponent = useMemo(() => {
    let index = currentStepIndex;

    return steps[index]?.element;
  }, [currentStepIndex, isMobile, stepKey]);

  // Validate URL params
  useEffect(() => {
    // const validType = ['doctor', 'clinic'];
    const type = searchParams.get('type');

    // if (!validType.includes(type)) {
    //   navigate('/not-found');
    //   return;
    // }

    let validFlows = [];
    let validStepKeys = [];

    if (type === 'doctor') {
      validStepKeys = ['chooseClinic', 'chooseTime', 'enterInfo'];

      const step = searchParams.get('step');
      const doctorId = searchParams.get('doctorId');

      if (!doctorId
        || !validStepKeys.includes(step)) {
        navigate('/not-found');
      }
    } else if (type === 'clinic') {
      validStepKeys = ['chooseDoctor', 'chooseTime', 'enterInfo'];
      validFlows = ['byDoctor', 'byTime'];

      const step = searchParams.get('step');
      const specialty = searchParams.get('specialty');

      if (!specialty || !validStepKeys.includes(step)) {
        navigate('/not-found');
      }
    }

  }, [searchParams, setSearchParams]);


  if (isMobile === undefined) return null;

  return (
    <div className='relative content-container h-full flex flex-col p-0 md:p-6 md:max-w-6xl md:mx-auto'>
      {(isMobile) ? (
        <div className="sticky top-0 z-50 flex items-center justify-between p-3 bg-gradient-to-r from-green-800 to-green-600">
          <div className="flex items-center space-x-4 w-full">
            <button
              className="flex items-center absolute top-1/2 -translate-y-1/2 left-4"
              onClick={() =>
                onBack(currentStepIndex > 0 ? steps[currentStepIndex - 1].key : 'home')
              }
            >
              <ArrowLeft className="w-6 h-6 text-white" />
            </button>
            <div className=' w-full flex justify-center'>
              <span className="text-base cursor-default font-medium text-white">
                {steps.find(step => step.key === stepKey)?.label ?? ''}
              </span>
            </div>
          </div>
        </div>
      ) : (
        stepKey !== 'selectSpecialty' && (
          <div className="mb-4">
            {/* icon back */}
            <button
              className="flex items-center space-x-2 hover:text-red-600 transition-colors duration-200"
              style={{ color: 'red' }}
              onClick={() =>
                onBack(currentStepIndex > 0 ? steps[currentStepIndex - 1].key : 'home')
              }
            >
              <Undo2 className="w-5 h-5" />
              <span className="font-medium text-base">{t('quaylai')}</span>
            </button>
          </div>
        )
      )}


      {isMobile && (
        <div className="content relative bg-white shadow-sm border-b">
          <StepProgress currentStep={currentStepIndex} />
        </div>)}

      {/* content */}
      {currentStepComponent}

    </div>
  );
};

export default BookingFlow;
